/*
 * Alidade PrimeNG preset (PrimeNG 18+ token theming; the app is on Angular 20).
 * Source: AlidadeAngularStyleGuide.html → "Wiring it into Angular" → "PrimeNG preset".
 *
 * Registered in app.config.ts with `darkModeSelector: '.al-dark'`. PrimeNG emits the dark
 * colour scheme under `:root.al-dark, :root .al-dark`, so during the screen-by-screen restyle
 * the class sits on the root element of each restyled page; once every screen is done it
 * moves to <html> and the app becomes dark-only.
 *
 * Token keys follow the Aura preset structure. Unknown keys are silently ignored, which is
 * the usual reason a colour "does not apply" — validate against the theming docs of the
 * installed PrimeNG version before relying on a new key. Everything tokens cannot express
 * (uppercase CTA buttons, capitalised table headers) lives in styles.scss as plain CSS.
 */
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const ink = '#f4ece0';
const inkMuted = '#b2b0c1';
const inkSubtle = '#8a8799';
const inkDisabled = '#7b7991';
const onAccent = '#1b0f22';
const hairline = 'rgba(255,255,255,.10)';
const hairlineStrong = 'rgba(255,255,255,.18)';
const inset = 'rgba(255,255,255,.055)';
const hover = 'rgba(255,255,255,.045)';

export const AlidadePreset = definePreset(Aura, {
  primitive: {
    borderRadius: { none: '0', xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px' },
    teal: {
      50: '#e6f6fa',
      100: '#c2ebf3',
      200: '#8fd9ea',
      300: '#5cc7e1',
      400: '#2ab5d9',
      500: '#009bc1',
      600: '#0089ab',
      700: '#00728f',
      800: '#005b72',
      900: '#004556',
      950: '#002e3a',
    },
    navy: {
      0: '#f4ece0',
      50: '#e7e6f0',
      100: '#c7c5d6',
      200: '#a6a4bb',
      300: '#8a8799',
      400: '#5f5d75',
      500: '#3a3a58',
      600: '#2b2c48',
      700: '#21223c',
      800: '#1a1c35',
      900: '#0f1a2f',
      950: '#091525',
    },
  },
  semantic: {
    primary: {
      50: '{teal.50}',
      100: '{teal.100}',
      200: '{teal.200}',
      300: '{teal.300}',
      400: '{teal.400}',
      500: '{teal.500}',
      600: '{teal.600}',
      700: '{teal.700}',
      800: '{teal.800}',
      900: '{teal.900}',
      950: '{teal.950}',
    },
    focusRing: { width: '2px', style: 'solid', color: '{primary.color}', offset: '2px', shadow: 'none' },
    /* Shared by every list-based overlay (select, multiselect, listbox, autocomplete, treeselect …):
       the same option geometry as `components.select` below. */
    list: {
      padding: '0.25rem',
      gap: '2px',
      header: { padding: '0.5rem 0.75rem 0.25rem 0.75rem' },
      option: { padding: '0.625rem 1rem', borderRadius: '6px' },
      optionGroup: { padding: '0.5rem 1rem', fontWeight: '600' },
    },
    overlay: {
      select: { borderRadius: '8px', shadow: '0 12px 32px rgba(0,0,0,.45)' },
      popover: { borderRadius: '8px', padding: '1rem', shadow: '0 12px 32px rgba(0,0,0,.45)' },
      modal: { borderRadius: '12px', padding: '1.5rem', shadow: '0 24px 64px rgba(0,0,0,.5)' },
    },
    formField: {
      paddingX: '0.875rem',
      paddingY: '0.625rem',
      borderRadius: '8px',
      focusRing: { width: '1px', style: 'solid', color: '{primary.color}', offset: '0', shadow: 'none' },
    },
    colorScheme: {
      dark: {
        surface: {
          0: '{navy.0}',
          50: '{navy.50}',
          100: '{navy.100}',
          200: '{navy.200}',
          300: '{navy.300}',
          400: '{navy.400}',
          500: '{navy.500}',
          600: '{navy.600}',
          700: '{navy.700}',
          800: '{navy.800}',
          900: '{navy.900}',
          950: '{navy.950}',
        },
        primary: { color: '#009bc1', contrastColor: onAccent, hoverColor: '#00b4de', activeColor: '#0089ab' },
        highlight: {
          background: 'rgba(0,155,193,.15)',
          focusBackground: 'rgba(0,155,193,.25)',
          color: '#009bc1',
          focusColor: '#00b4de',
        },
        mask: { background: 'rgba(0,0,0,.6)', color: ink },
        text: { color: ink, hoverColor: ink, mutedColor: inkMuted, hoverMutedColor: ink },
        content: { background: '#1a1c35', hoverBackground: hover, borderColor: hairline, color: ink, hoverColor: ink },
        overlay: {
          select: { background: '#21223c', borderColor: hairlineStrong, color: ink },
          popover: { background: '#21223c', borderColor: hairlineStrong, color: ink },
          modal: { background: '#21223c', borderColor: hairlineStrong, color: ink },
        },
        formField: {
          background: inset,
          disabledBackground: 'rgba(255,255,255,.03)',
          filledBackground: inset,
          filledHoverBackground: inset,
          filledFocusBackground: inset,
          borderColor: hairline,
          hoverBorderColor: hairlineStrong,
          focusBorderColor: '#009bc1',
          invalidBorderColor: '#f87171',
          color: ink,
          disabledColor: inkDisabled,
          placeholderColor: inkSubtle,
          invalidPlaceholderColor: '#f87171',
          floatLabelColor: inkSubtle,
          floatLabelFocusColor: '#009bc1',
          floatLabelActiveColor: inkSubtle,
          floatLabelInvalidColor: '#f87171',
          iconColor: inkSubtle,
          shadow: 'none',
        },
        list: {
          option: {
            focusBackground: hover,
            selectedBackground: 'rgba(0,155,193,.15)',
            selectedFocusBackground: 'rgba(0,155,193,.25)',
            color: inkMuted,
            focusColor: ink,
            selectedColor: '#009bc1',
            selectedFocusColor: '#00b4de',
            icon: { color: inkSubtle, focusColor: ink },
          },
          optionGroup: { background: 'transparent', color: inkSubtle },
        },
        navigation: {
          item: {
            focusBackground: hover,
            activeBackground: 'rgba(0,155,193,.15)',
            color: inkMuted,
            focusColor: ink,
            activeColor: '#009bc1',
            icon: { color: inkSubtle, focusColor: ink, activeColor: '#009bc1' },
          },
          submenuLabel: { background: 'transparent', color: inkSubtle },
          submenuIcon: { color: inkSubtle, focusColor: ink, activeColor: '#009bc1' },
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '8px',
        paddingX: '1.25rem',
        paddingY: '0.625rem',
        gap: '0.5rem',
        label: { fontWeight: '500' },
        sm: { fontSize: '0.75rem', paddingX: '0.75rem', paddingY: '0.375rem' },
        lg: { fontSize: '1rem', paddingX: '1.5rem', paddingY: '0.75rem' },
      },
      colorScheme: {
        dark: {
          root: {
            primary: {
              background: '#009bc1',
              hoverBackground: '#00b4de',
              activeBackground: '#0089ab',
              borderColor: '#009bc1',
              hoverBorderColor: '#00b4de',
              activeBorderColor: '#0089ab',
              color: onAccent,
              hoverColor: onAccent,
              activeColor: onAccent,
            },
            secondary: {
              background: '#21223c',
              hoverBackground: 'rgba(255,255,255,.075)',
              activeBackground: '#21223c',
              borderColor: hairline,
              hoverBorderColor: hairlineStrong,
              activeBorderColor: hairlineStrong,
              color: ink,
              hoverColor: ink,
              activeColor: ink,
            },
            danger: {
              background: '#f87171',
              hoverBackground: '#fa8585',
              activeBackground: '#f25c5c',
              borderColor: '#f87171',
              hoverBorderColor: '#fa8585',
              activeBorderColor: '#f25c5c',
              color: onAccent,
              hoverColor: onAccent,
              activeColor: onAccent,
            },
            success: {
              background: '#4ade80',
              hoverBackground: '#5fe391',
              activeBackground: '#37d070',
              borderColor: '#4ade80',
              hoverBorderColor: '#5fe391',
              activeBorderColor: '#37d070',
              color: onAccent,
              hoverColor: onAccent,
              activeColor: onAccent,
            },
          },
          text: {
            primary: {
              hoverBackground: 'rgba(0,155,193,.15)',
              activeBackground: 'rgba(0,155,193,.25)',
              color: '#009bc1',
            },
            secondary: { hoverBackground: hover, activeBackground: hover, color: inkMuted },
            danger: {
              hoverBackground: 'rgba(248,113,113,.15)',
              activeBackground: 'rgba(248,113,113,.25)',
              color: '#f87171',
            },
          },
          outlined: {
            primary: {
              hoverBackground: 'rgba(0,155,193,.15)',
              activeBackground: 'rgba(0,155,193,.25)',
              borderColor: 'rgba(0,155,193,.45)',
              color: '#009bc1',
            },
            secondary: { hoverBackground: hover, activeBackground: hover, borderColor: hairline, color: ink },
          },
        },
      },
    },
    datatable: {
      root: { borderColor: hairline },
      header: { background: 'transparent', borderColor: hairline, color: ink, padding: '0 0 1rem 0' },
      headerCell: {
        background: inset,
        hoverBackground: inset,
        selectedBackground: inset,
        borderColor: hairline,
        color: inkMuted,
        hoverColor: ink,
        padding: '0.625rem 1rem',
        focusRing: { shadow: 'none' },
      },
      columnTitle: { fontWeight: '500' },
      row: {
        background: 'transparent',
        hoverBackground: hover,
        selectedBackground: 'rgba(0,155,193,.10)',
        color: ink,
        hoverColor: ink,
        selectedColor: ink,
      },
      bodyCell: { borderColor: hairline, padding: '0.625rem 1rem' },
      footerCell: { background: 'transparent', borderColor: hairline, color: inkMuted },
      paginatorTop: { borderColor: hairline },
      paginatorBottom: { borderColor: hairline },
      colorScheme: { dark: { root: { borderColor: hairline }, sortIcon: { color: inkSubtle, hoverColor: ink } } },
    },
    paginator: {
      root: { background: 'transparent', color: inkMuted, gap: '0.25rem' },
      navButton: {
        background: 'transparent',
        hoverBackground: hover,
        selectedBackground: 'rgba(0,155,193,.15)',
        color: inkMuted,
        hoverColor: ink,
        selectedColor: '#009bc1',
        borderRadius: '8px',
        width: '2.25rem',
        height: '2.25rem',
      },
    },
    dialog: {
      root: {
        background: '#21223c',
        borderColor: hairlineStrong,
        color: ink,
        borderRadius: '12px',
        shadow: '0 24px 64px rgba(0,0,0,.5)',
      },
      header: { padding: '1.25rem 1.5rem', gap: '0.5rem' },
      title: { fontSize: '1.25rem', fontWeight: '600' },
      content: { padding: '1.5rem' },
      footer: { padding: '1rem 1.5rem', gap: '0.75rem' },
    },
    confirmdialog: { icon: { size: '1.5rem', color: '#fbbf24' } },
    select: {
      root: {
        background: inset,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        focusBorderColor: '#009bc1',
        color: ink,
        placeholderColor: inkSubtle,
        borderRadius: '8px',
        shadow: 'none',
      },
      overlay: {
        background: '#21223c',
        borderColor: hairlineStrong,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
      },
      option: {
        focusBackground: hover,
        selectedBackground: 'rgba(0,155,193,.15)',
        selectedFocusBackground: 'rgba(0,155,193,.25)',
        color: inkMuted,
        focusColor: ink,
        selectedColor: '#009bc1',
        padding: '0.625rem 1rem',
        borderRadius: '6px',
      },
      dropdown: { color: inkSubtle },
    },
    multiselect: {
      root: {
        background: inset,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        focusBorderColor: '#009bc1',
        color: ink,
        placeholderColor: inkSubtle,
        borderRadius: '8px',
        shadow: 'none',
      },
      dropdown: { color: inkSubtle },
      overlay: {
        background: '#21223c',
        borderColor: hairlineStrong,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
      },
      list: { padding: '0.25rem', gap: '2px', header: { padding: '0.5rem 0.75rem 0.25rem 0.75rem' } },
      option: {
        focusBackground: hover,
        selectedBackground: 'rgba(0,155,193,.15)',
        selectedFocusBackground: 'rgba(0,155,193,.25)',
        color: inkMuted,
        focusColor: ink,
        selectedColor: '#009bc1',
        selectedFocusColor: '#00b4de',
        padding: '0.625rem 1rem',
        borderRadius: '6px',
        gap: '0.5rem',
      },
      optionGroup: { background: 'transparent', color: inkSubtle, fontWeight: '600', padding: '0.5rem 1rem' },
      chip: { borderRadius: '6px' },
      clearIcon: { color: inkSubtle },
      emptyMessage: { padding: '0.625rem 1rem' },
    },
    chip: {
      root: {
        background: 'rgba(0,155,193,.15)',
        color: '#009bc1',
        borderRadius: '6px',
        paddingX: '0.625rem',
        paddingY: '0.25rem',
      },
      removeIcon: { color: '#009bc1', focusRing: { shadow: 'none' } },
    },
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '500',
        padding: '0.125rem 0.625rem',
        gap: '0.375rem',
        borderRadius: '9999px',
        roundedBorderRadius: '9999px',
      },
      colorScheme: {
        dark: {
          primary: { background: 'rgba(0,155,193,.15)', color: '#009bc1' },
          secondary: { background: inset, color: ink },
          success: { background: 'rgba(74,222,128,.15)', color: '#4ade80' },
          info: { background: 'rgba(96,165,250,.15)', color: '#60a5fa' },
          warn: { background: 'rgba(251,191,36,.15)', color: '#fbbf24' },
          danger: { background: 'rgba(248,113,113,.15)', color: '#f87171' },
          contrast: { background: ink, color: onAccent },
        },
      },
    },
    badge: {
      root: { borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600' },
      colorScheme: {
        dark: {
          primary: { background: '#009bc1', color: onAccent },
          secondary: { background: inset, color: inkMuted },
          success: { background: '#4ade80', color: onAccent },
          warn: { background: '#fbbf24', color: onAccent },
          danger: { background: '#f87171', color: onAccent },
          info: { background: '#60a5fa', color: onAccent },
        },
      },
    },
    toggleswitch: {
      root: {
        width: '2.25rem',
        height: '1.25rem',
        gap: '0.25rem',
        borderRadius: '9999px',
        background: '#21223c',
        hoverBackground: '#21223c',
        checkedBackground: '#009bc1',
        checkedHoverBackground: '#00b4de',
        borderColor: hairlineStrong,
        hoverBorderColor: hairlineStrong,
        checkedBorderColor: '#009bc1',
        checkedHoverBorderColor: '#00b4de',
        shadow: 'none',
        focusRing: { width: '2px', style: 'solid', color: '#009bc1', offset: '2px', shadow: 'none' },
      },
      handle: {
        size: '0.875rem',
        background: ink,
        hoverBackground: ink,
        checkedBackground: ink,
        checkedHoverBackground: ink,
        color: onAccent,
        hoverColor: onAccent,
        checkedColor: onAccent,
        checkedHoverColor: onAccent,
      },
    },
    checkbox: {
      root: {
        width: '1rem',
        height: '1rem',
        borderRadius: '4px',
        background: 'transparent',
        checkedBackground: '#009bc1',
        checkedHoverBackground: '#00b4de',
        borderColor: 'rgba(255,255,255,.22)',
        hoverBorderColor: 'rgba(255,255,255,.4)',
        checkedBorderColor: '#009bc1',
        checkedHoverBorderColor: '#00b4de',
        focusBorderColor: '#009bc1',
        shadow: 'none',
      },
      icon: { size: '0.625rem', color: onAccent, checkedColor: onAccent, checkedHoverColor: onAccent },
    },
    radiobutton: {
      root: {
        width: '1rem',
        height: '1rem',
        background: 'transparent',
        checkedBackground: '#009bc1',
        checkedHoverBackground: '#00b4de',
        borderColor: 'rgba(255,255,255,.22)',
        hoverBorderColor: 'rgba(255,255,255,.4)',
        checkedBorderColor: '#009bc1',
        checkedHoverBorderColor: '#00b4de',
        shadow: 'none',
      },
      icon: { size: '0.5rem', checkedColor: onAccent, checkedHoverColor: onAccent },
    },
    tabs: {
      tablist: { background: 'transparent', borderColor: hairline, borderWidth: '0 0 1px 0' },
      tab: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        borderWidth: '0 0 2px 0',
        borderColor: 'transparent',
        hoverBorderColor: hairlineStrong,
        activeBorderColor: '#009bc1',
        color: inkMuted,
        hoverColor: ink,
        activeColor: '#009bc1',
        padding: '0.625rem 1rem',
        fontWeight: '500',
        gap: '0.5rem',
        focusRing: { shadow: 'none' },
      },
      tabpanel: { background: 'transparent', color: ink, padding: '1.5rem 0 0 0' },
      activeBar: { height: '2px', bottom: '-1px', background: '#009bc1' },
    },
    panel: {
      root: { background: '#1a1c35', borderColor: hairline, color: ink, borderRadius: '12px' },
      header: {
        background: 'transparent',
        borderColor: hairline,
        color: ink,
        padding: '1rem 1.5rem',
        borderWidth: '0 0 1px 0',
      },
      content: { padding: '1rem 1.5rem' },
      title: { fontWeight: '600' },
    },
    card: {
      root: { background: '#1a1c35', color: ink, borderRadius: '12px', shadow: 'none' },
      body: { padding: '1.5rem', gap: '0.75rem' },
      title: { fontSize: '1.25rem', fontWeight: '400' },
      subtitle: { color: inkMuted },
    },
    menu: {
      root: {
        background: '#21223c',
        borderColor: hairline,
        color: inkMuted,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
      },
      list: { padding: '0.25rem 0', gap: '0' },
      item: {
        focusBackground: hover,
        color: inkMuted,
        focusColor: ink,
        padding: '0.5rem 1rem',
        borderRadius: '0',
        gap: '0.5rem',
        icon: { color: inkSubtle, focusColor: ink },
      },
      submenuLabel: { color: inkSubtle, fontWeight: '600', padding: '0.5rem 1rem' },
      separator: { borderColor: hairline },
    },
    contextmenu: {
      root: {
        background: '#21223c',
        borderColor: hairlineStrong,
        color: inkMuted,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
      },
    },
    popover: {
      root: {
        background: '#21223c',
        borderColor: hairlineStrong,
        color: ink,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
      },
      content: { padding: '1rem' },
    },
    tooltip: {
      root: {
        background: '#21223c',
        color: ink,
        borderRadius: '6px',
        shadow: '0 8px 24px rgba(0,0,0,.4)',
        padding: '0.375rem 0.625rem',
        gutter: '0.25rem',
      },
    },
    toast: {
      root: { width: '24rem', borderRadius: '12px', borderWidth: '1px' },
      content: { padding: '1rem', gap: '0.75rem' },
      summary: { fontWeight: '600', fontSize: '0.875rem' },
      detail: { fontWeight: '400', fontSize: '0.8125rem' },
      colorScheme: {
        dark: {
          success: {
            background: 'rgba(74,222,128,.12)',
            borderColor: 'rgba(74,222,128,.4)',
            color: '#4ade80',
            detailColor: ink,
            shadow: '0 12px 32px rgba(0,0,0,.45)',
            closeButton: { hoverBackground: hover, focusRing: { shadow: 'none' } },
          },
          info: {
            background: 'rgba(96,165,250,.12)',
            borderColor: 'rgba(96,165,250,.4)',
            color: '#60a5fa',
            detailColor: ink,
            shadow: '0 12px 32px rgba(0,0,0,.45)',
            closeButton: { hoverBackground: hover, focusRing: { shadow: 'none' } },
          },
          warn: {
            background: 'rgba(251,191,36,.12)',
            borderColor: 'rgba(251,191,36,.4)',
            color: '#fbbf24',
            detailColor: ink,
            shadow: '0 12px 32px rgba(0,0,0,.45)',
            closeButton: { hoverBackground: hover, focusRing: { shadow: 'none' } },
          },
          error: {
            background: 'rgba(248,113,113,.12)',
            borderColor: 'rgba(248,113,113,.4)',
            color: '#f87171',
            detailColor: ink,
            shadow: '0 12px 32px rgba(0,0,0,.45)',
            closeButton: { hoverBackground: hover, focusRing: { shadow: 'none' } },
          },
        },
      },
    },
    message: {
      root: { borderRadius: '8px', borderWidth: '1px' },
      content: { padding: '0.75rem 1rem', gap: '0.5rem' },
      text: { fontSize: '0.875rem', fontWeight: '400' },
      colorScheme: {
        dark: {
          info: { background: 'rgba(96,165,250,.15)', borderColor: 'rgba(96,165,250,.4)', color: '#60a5fa' },
          success: { background: 'rgba(74,222,128,.15)', borderColor: 'rgba(74,222,128,.4)', color: '#4ade80' },
          warn: { background: 'rgba(251,191,36,.15)', borderColor: 'rgba(251,191,36,.4)', color: '#fbbf24' },
          error: { background: 'rgba(248,113,113,.15)', borderColor: 'rgba(248,113,113,.4)', color: '#f87171' },
          secondary: { background: inset, borderColor: hairline, color: inkMuted },
        },
      },
    },
    skeleton: {
      root: { borderRadius: '8px' },
      colorScheme: {
        dark: { root: { background: 'rgba(255,255,255,.06)', animationBackground: 'rgba(255,255,255,.12)' } },
      },
    },
    progressbar: {
      root: { background: inset, borderRadius: '9999px', height: '0.5rem' },
      value: { background: '#009bc1' },
      label: { color: onAccent, fontSize: '0.75rem', fontWeight: '600' },
    },
    progressspinner: {
      colorScheme: {
        dark: { root: { colorOne: '#009bc1', colorTwo: '#009bc1', colorThree: '#009bc1', colorFour: '#009bc1' } },
      },
    },
    divider: { root: { borderColor: hairline }, content: { background: '#1a1c35', color: inkSubtle } },
    datepicker: {
      panel: {
        background: '#21223c',
        borderColor: hairlineStrong,
        color: ink,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
        padding: '0.75rem',
      },
      header: { background: 'transparent', borderColor: hairline, color: ink, padding: '0 0 0.5rem 0' },
      title: { gap: '0.5rem', fontWeight: '600' },
      dropdown: {
        background: inset,
        hoverBackground: 'rgba(255,255,255,.08)',
        activeBackground: 'rgba(255,255,255,.08)',
        color: inkSubtle,
        hoverColor: ink,
        activeColor: ink,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        activeBorderColor: '#009bc1',
        borderRadius: '8px',
      },
      weekDay: { color: '#c7b9ac', fontWeight: '500', padding: '0.25rem' },
      date: {
        hoverBackground: 'rgba(255,255,255,.085)',
        selectedBackground: '#009bc1',
        rangeSelectedBackground: 'rgba(0,155,193,.15)',
        color: ink,
        hoverColor: ink,
        selectedColor: onAccent,
        rangeSelectedColor: ink,
        width: '2.25rem',
        height: '2.25rem',
        borderRadius: '8px',
        padding: '0',
        focusRing: { width: '2px', style: 'solid', color: '#009bc1', offset: '1px', shadow: 'none' },
      },
      today: { background: 'transparent', color: ink },
      monthView: { margin: '0.5rem 0 0 0' },
      month: { padding: '0.375rem', borderRadius: '8px' },
      yearView: { margin: '0.5rem 0 0 0' },
      year: { padding: '0.375rem', borderRadius: '8px' },
      buttonbar: { padding: '0.5rem 0 0 0', borderColor: hairline },
      timePicker: { padding: '0.5rem 0 0 0', borderColor: hairline, gap: '0.5rem', buttonGap: '0.25rem' },
    },
    steps: {
      colorScheme: {
        dark: {
          itemLink: { focusRing: { shadow: 'none' } },
          itemNumber: {
            background: '#1a1c35',
            activeBackground: '#009bc1',
            borderColor: hairlineStrong,
            activeBorderColor: '#009bc1',
            color: inkSubtle,
            activeColor: onAccent,
            size: '1.75rem',
            fontSize: '0.75rem',
            fontWeight: '600',
          },
          itemLabel: { color: inkSubtle, activeColor: '#009bc1', fontWeight: '600' },
          separator: { background: hairline },
        },
      },
    },
    editor: {
      toolbar: { background: inset, borderColor: hairline, borderRadius: '8px 8px 0 0' },
      toolbarItem: { color: inkMuted, hoverColor: ink, activeColor: '#009bc1' },
      content: { background: inset, borderColor: hairline, color: ink, borderRadius: '0 0 8px 8px' },
      overlay: {
        background: '#21223c',
        borderColor: hairlineStrong,
        borderRadius: '8px',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
        padding: '0.25rem 0',
      },
      overlayOption: {
        focusBackground: hover,
        color: inkMuted,
        focusColor: ink,
        padding: '0.5rem 1rem',
        borderRadius: '0',
      },
    },
    inputnumber: {
      button: { width: '2.25rem', borderRadius: '6px', verticalPadding: '0.25rem' },
      colorScheme: {
        dark: {
          button: {
            background: 'transparent',
            hoverBackground: hover,
            activeBackground: hover,
            borderColor: hairline,
            hoverBorderColor: hairlineStrong,
            activeBorderColor: hairlineStrong,
            color: inkSubtle,
            hoverColor: ink,
            activeColor: ink,
          },
        },
      },
    },
    autocomplete: {
      dropdown: {
        background: inset,
        hoverBackground: 'rgba(255,255,255,.08)',
        activeBackground: 'rgba(255,255,255,.08)',
        color: inkSubtle,
        hoverColor: ink,
        activeColor: ink,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        activeBorderColor: '#009bc1',
        borderRadius: '8px',
      },
      chip: { borderRadius: '6px' },
    },
    listbox: {
      root: { background: inset, borderColor: hairline, color: ink, borderRadius: '8px', shadow: 'none' },
      option: {
        focusBackground: hover,
        selectedBackground: 'rgba(0,155,193,.15)',
        selectedFocusBackground: 'rgba(0,155,193,.25)',
        color: inkMuted,
        focusColor: ink,
        selectedColor: '#009bc1',
        padding: '0.625rem 1rem',
        borderRadius: '6px',
      },
    },
    togglebutton: {
      root: {
        background: inset,
        checkedBackground: 'rgba(0,155,193,.15)',
        hoverBackground: 'rgba(255,255,255,.075)',
        borderColor: hairline,
        color: inkMuted,
        hoverColor: ink,
        checkedColor: '#009bc1',
        checkedBorderColor: 'rgba(0,155,193,.45)',
        borderRadius: '8px',
      },
      content: { checkedBackground: 'transparent', checkedShadow: 'none' },
    },
    slider: {
      track: { background: inset, borderRadius: '9999px', size: '0.375rem' },
      range: { background: '#009bc1' },
      handle: {
        width: '1.125rem',
        height: '1.125rem',
        borderRadius: '9999px',
        background: ink,
        hoverBackground: ink,
        content: { background: ink, hoverBackground: ink, width: '0.75rem', height: '0.75rem', shadow: 'none' },
      },
    },
    metergroup: {
      root: { borderRadius: '9999px', gap: '1rem' },
      meters: { background: inset, size: '0.5rem' },
      label: { gap: '0.5rem' },
      labelMarker: { size: '0.5rem' },
      labelIcon: { size: '1rem' },
    },
    panelmenu: {
      root: { gap: '0.25rem' },
      panel: {
        background: 'transparent',
        borderColor: 'transparent',
        borderWidth: '0',
        color: inkMuted,
        padding: '0',
        borderRadius: '8px',
        first: { borderWidth: '0', topBorderRadius: '8px' },
        last: { borderWidth: '0', bottomBorderRadius: '8px' },
      },
      item: {
        focusBackground: hover,
        color: inkMuted,
        focusColor: ink,
        gap: '0.5rem',
        padding: '0.625rem 1.5rem',
        borderRadius: '0',
        icon: { color: inkSubtle, focusColor: ink },
      },
      submenu: { indent: '1rem' },
      submenuIcon: { color: inkSubtle, focusColor: ink },
    },
    floatlabel: {
      root: {
        color: inkSubtle,
        focusColor: '#009bc1',
        activeColor: inkSubtle,
        invalidColor: '#f87171',
        fontWeight: '500',
      },
    },
    iconfield: { icon: { color: inkSubtle } },
    inputtext: {
      root: {
        background: inset,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        focusBorderColor: '#009bc1',
        color: ink,
        placeholderColor: inkSubtle,
        borderRadius: '8px',
        shadow: 'none',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
    },
    textarea: {
      root: {
        background: inset,
        borderColor: hairline,
        hoverBorderColor: hairlineStrong,
        focusBorderColor: '#009bc1',
        color: ink,
        placeholderColor: inkSubtle,
        borderRadius: '8px',
        shadow: 'none',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
    },
  },
});
